import XDate from 'xdate';
import {parse_us_phone_number, valid_email} from '~/common/utils';

export const selectDetails = ({details}) => details;
export const selectRequestFailed = ({details}) => details.request.failed;

/** Required fields: [host, start, title, type] */
export const selectTitle = ({details}) => details.title.trim();
export const selectEditTitle = ({details}) => details.title;
export const selectType = ({details}) => details.type;
export const selectHost = ({details}) => details.host;
export const selectStart = ({details}) => details.start;
export const selectEnd = ({details}) => details.end;
export const selectTimezone = ({details}) => details.known_timezone_abbr;
export const selectRsvpOff = ({details}) => details.rsvp_off;

export const selectAddress = ({details}) => details.address;
export const selectLocation = ({details}) => details.location;
export const selectPlaceId = ({details}) => details.placeId;
export const selectMeetingUrl = ({details}) => details.meetingUrl;
export const selectShowMap = ({details}) => details.showMap;

export const selectTitleError = ({details}) =>
  selectRequestFailed({details}) && !selectTitle({details});
export const selectTypeError = ({details}) =>
  selectRequestFailed({details}) && !selectType({details});
export const selectHostError = ({details}) =>
  selectRequestFailed({details}) && !selectHost({details});
export const selectRequiredComplete = ({details}) =>
  details.title.trim() && details.host && details.start;

/** Optional fields: [co-host, end] */
export const selectCohostName = ({details}) => details.cohost.name;
export const selectCohostEmail = ({details}) => details.cohost.email;

export const selectCohostEmailValid = ({details}) =>
  valid_email(selectCohostEmail({details})).is_valid;
export const selectCohostNameError = ({details}) =>
  selectCohostEmailValid({details}) && !selectCohostName({details});
const selectCohostEmailMissing = ({details}) =>
  selectCohostName({details}) && !selectCohostEmailValid({details});
export const selectValidCohost = ({details}) =>
  !selectCohostEmailMissing({details}) && !selectCohostNameError({details});

export const selectCoHost = ({details}) => details.cohost;

const gohPlaceholder = [{name: '', email: ''}];
export const selectGuestsOfHonor = ({details}) =>
  details.guestsOfHonor?.guests?.length ? details.guestsOfHonor?.guests : gohPlaceholder;
export const selectGuestsOfHonorUpdated = ({details}) => details.guestsOfHonor?.update;

export const selectHasGuestsOfHonor = ({details}) => details.guestsOfHonor?.guests?.length > 0;
export const selectPhoneNumber = ({details}) => String(details.phoneNumber);
export const selectPhoneNumberIsValid = ({details}) =>
  !selectPhoneNumber({details}) ||
  (selectPhoneNumber({details}) && parse_us_phone_number(selectPhoneNumber({details})).is_valid);

export const selectStartDateBeforeEndDate = ({details}) => {
  const {start, end} = details;
  if (!end) return false;
  return new Date(end).getTime() - new Date(start).getTime() < 0;
};

/** Allow submission */
export const selectIsDirty = ({details}) => {
  const {address, cohost, time, timezone, request, ...rest} = details;

  const flattened = {...address, ...cohost, ...time, ...rest};

  return !Object.values(flattened).some((id) => id);
};

export const selectEventDateHasPassed = ({details}) => {
  const {start} = details;

  const then = new XDate(start);
  const now = new XDate();
  const diff = now.diffDays(then);

  return diff < 0;
};

export const selectStartDateError = ({details}) =>
  selectRequestFailed({details}) &&
  (!selectStart({details}) || selectEventDateHasPassed({details}));

export const selectStartDateRequestError = ({details}) =>
  selectRequestFailed({details}) && !selectStart({details});

/** Validate on submit/navigation */
export const selectEventDetailsValid = ({details}, onlyCheckTimeIfExists) => {
  if (onlyCheckTimeIfExists) {
    if (selectStart({details})) {
      if (selectEventDateHasPassed({details})) return false;
      if (selectEnd({details})) {
        if (selectStartDateBeforeEndDate({details})) return false;
      }
    }
    return true;
  }

  if (selectEventDateHasPassed({details})) return false;
  if (selectStartDateBeforeEndDate({details})) return false;
  if (!selectValidCohost({details})) return false;
  if (!selectPhoneNumberIsValid({details})) return false;
  return selectRequiredComplete({details});
};

export const eventIsPremium = ({details}) => details.isPremium;
export const eventInEditMode = ({details, ecard}) => details?.inEditMode ?? ecard?.isInEditMode;
export const eventInUpsellMode = ({details}) => details.isUpsellMode;
export const selectUsedAs = ({details}) => details.usedAs;
